import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Filter, Download, MoreHorizontal } from 'lucide-react';
interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
}
interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  title?: string;
  searchPlaceholder?: string;
  actions?: (row: T) => React.ReactNode;
}
export function DataTable<T extends {
  id: string | number;
}>({
  data,
  columns,
  title,
  searchPlaceholder = 'Search...',
  actions
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;
  // Filter logic would go here in a real app
  const filteredData = data;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-25">
      {(title || searchPlaceholder) && <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-26">
          {title && <h3 className="text-lg font-semibold text-slate-800" data-id="element-27">{title}</h3>}

          <div className="flex items-center gap-3" data-id="element-28">
            <div className="relative" data-id="element-29">
              <input type="text" placeholder={searchPlaceholder} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-4 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold w-full sm:w-64" data-id="element-30" />
            </div>
            <button className="p-2 text-slate-500 hover:bg-slate-50 rounded-lg border border-slate-200" data-id="element-31">
              <Filter size={18} data-id="element-32" />
            </button>
            <button className="p-2 text-slate-500 hover:bg-slate-50 rounded-lg border border-slate-200" data-id="element-33">
              <Download size={18} data-id="element-34" />
            </button>
          </div>
        </div>}

      <div className="overflow-x-auto" data-id="element-35">
        <table className="w-full text-left border-collapse" data-id="element-36">
          <thead data-id="element-37">
            <tr className="bg-slate-50/50 border-b border-slate-100" data-id="element-38">
              {columns.map((col, idx) => <th key={idx} className={`px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider ${col.className || ''}`} data-id="element-39">
                  {col.header}
                </th>)}
              {actions && <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right" data-id="element-40">
                  Actions
                </th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-41">
            {currentData.map(row => <tr key={row.id} className="hover:bg-slate-50/50 transition-colors group" data-id="element-42">
                {columns.map((col, idx) => <td key={idx} className={`px-6 py-4 text-sm text-slate-700 whitespace-nowrap ${col.className || ''}`} data-id="element-43">
                    {typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor] as React.ReactNode}
                  </td>)}
                {actions && <td className="px-6 py-4 text-right whitespace-nowrap" data-id="element-44">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-45">
                      {actions(row)}
                    </div>
                  </td>}
              </tr>)}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between" data-id="element-46">
        <p className="text-sm text-slate-500" data-id="element-47">
          Showing{' '}
          <span className="font-medium" data-id="element-48">
            {(currentPage - 1) * itemsPerPage + 1}
          </span>{' '}
          to{' '}
          <span className="font-medium" data-id="element-49">
            {Math.min(currentPage * itemsPerPage, filteredData.length)}
          </span>{' '}
          of <span className="font-medium" data-id="element-50">{filteredData.length}</span> results
        </p>
        <div className="flex items-center gap-2" data-id="element-51">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-52">
            <ChevronLeft size={20} className="text-slate-500" data-id="element-53" />
          </button>
          <span className="text-sm text-slate-600 font-medium px-2" data-id="element-54">
            Page {currentPage} of {totalPages}
          </span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-55">
            <ChevronRight size={20} className="text-slate-500" data-id="element-56" />
          </button>
        </div>
      </div>
    </div>;
}